import { useState, useMemo, useEffect, useRef } from 'react';
import { formatCurrency } from '../../../utils/formatters';
import { maskAccountNumber } from '../../../utils/securityUtils';
import StatementUpload from './StatementUpload';
import walletService from '../services/walletService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  ChevronUpIcon, 
  ChevronDownIcon, 
  LinkIcon, 
  XMarkIcon, 
  UserGroupIcon,
  BuildingLibraryIcon,
  ClockIcon,
  CreditCardIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import Portal from '../../../components/shared/Portal';
import { useAuthContext } from '../../auth/context/AuthContext';
import { isLoanComplete, getMissingLoanFields } from '../utils/loanCompletionUtils';

// Helper function to format debt (negative values)
const formatDebt = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(-Math.abs(amount)); // Always show as negative
};

// DisconnectModal component
const DisconnectModal = ({ isOpen, onClose, onDisconnect, onDisconnectWithTransactions, accountName }) => {
  if (!isOpen) return null;
  
  return (
    <Portal>
      <div className="fixed inset-0 z-50 overflow-auto bg-gray-500 bg-opacity-50 flex items-center justify-center">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 border border-gray-100">
          <div className="absolute top-4 right-4">
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Disconnect {accountName}
          </h3>
          
          <p className="text-sm text-gray-500 mb-6">
            How would you like to handle this account and its data?
          </p>
          
          <div className="space-y-3">
            <button
              onClick={() => onDisconnectWithTransactions()} 
              className="w-full px-4 py-2.5 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm font-medium text-left hover:bg-red-100 transition-colors"
            >
              Delete account and all transactions
              <p className="text-xs text-gray-500 mt-1">
                This will permanently remove the account and all its transaction history from MnyMny.
              </p>
            </button>
            
            <button
              onClick={() => onDisconnect()} 
              className="w-full px-4 py-2.5 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-lg text-sm font-medium text-left hover:bg-indigo-100 transition-colors"
            >
              Just disconnect the account
              <p className="text-xs text-gray-500 mt-1">
                This will remove the account but keep its transactions in MnyMny.
              </p>
            </button>
            
            <button
              onClick={onClose} 
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 text-gray-700 rounded-lg text-sm font-medium text-center hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

// LoanDetailsForm component
const LoanDetailsForm = ({ account, onSave, onCancel, isLoading }) => {
  // Helper function to format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    } catch (e) {
      console.error('Error formatting date:', e);
      return '';
    }
  };

  // Get initial values from account if available
  const initialValues = {
    interestRate: account.loanDetails?.interestRate || account.interestRate || '',
    originalPrincipal: account.loanDetails?.originalPrincipal || account.originalAmount || '',
    paymentAmount: account.loanDetails?.paymentAmount || account.monthlyPayment || '',
    term: account.loanDetails?.term || account.termLength || '',
    assetCategory: account.loanDetails?.assetCategory || account.assetCategory || '',
    ownershipType: account.loanDetails?.ownershipType || account.loanOwnershipType || '', // New: Lease, Own, Financed
    originatedDate: formatDateForInput(account.loanDetails?.originatedDate || account.originatedDate),
    // NEW: Asset value fields
    purchasePrice: account.loanDetails?.purchasePrice || account.purchasePrice || account.metadata?.purchasePrice || '',
    currentValue: account.loanDetails?.currentValue || account.currentValue || account.metadata?.currentValue || '',
    // NEW: Lease-specific fields
    leaseEndDate: formatDateForInput(account.loanDetails?.leaseEndDate || account.leaseEndDate || account.metadata?.leaseEndDate),
    buyoutPrice: account.loanDetails?.buyoutPrice || account.buyoutPrice || account.metadata?.buyoutPrice || '',
    mileageLimit: account.loanDetails?.mileageLimit || account.mileageLimit || account.metadata?.mileageLimit || ''
  };

  const [formData, setFormData] = useState(initialValues);

  // Debug: Log form data to see why fields might not show
  console.log('🔍 LoanDetailsForm - Current formData:', {
    assetCategory: formData.assetCategory,
    ownershipType: formData.ownershipType,
    shouldShowAssetFields: (formData.assetCategory === 'home' || formData.assetCategory === 'investment' || formData.assetCategory === 'vehicle') && formData.ownershipType !== 'lease',
    purchasePrice: formData.purchasePrice,
    currentValue: formData.currentValue
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert numeric fields to numbers
    const numericFields = ['interestRate', 'originalPrincipal', 'paymentAmount', 'purchasePrice', 'currentValue', 'buyoutPrice', 'mileageLimit'];
    if (numericFields.includes(name)) {
      setFormData({
        ...formData,
        [name]: value === '' ? '' : parseFloat(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  // Asset category options
  const assetCategories = [
    { value: 'home', label: 'Home / Primary Residence' },
    { value: 'vehicle', label: 'Vehicle / Auto' },
    { value: 'investment', label: 'Investment Property' },
    { value: 'education', label: 'Education / Student Loan' },
    { value: 'personal', label: 'Personal Loan' },
    { value: 'other', label: 'Other Asset' }
  ];

  // Ownership types vary by asset category
  const getOwnershipTypes = () => {
    const assetCat = formData.assetCategory;
    
    if (assetCat === 'vehicle') {
      // Vehicles can be financed, leased, or owned
      return [
        { value: 'financed', label: 'Financed (Auto Loan)' },
        { value: 'lease', label: 'Lease' },
        { value: 'owned', label: 'Owned Outright' }
      ];
    } else if (assetCat === 'home' || assetCat === 'investment') {
      // Real estate - no "lease" option (that's renting, not owning)
      return [
        { value: 'financed', label: 'Mortgaged' },
        { value: 'owned', label: 'Owned Outright (Paid Off)' }
      ];
    } else {
      // Other assets (education, personal, business, etc.)
      return [
        { value: 'financed', label: 'Financed (Loan)' },
        { value: 'owned', label: 'Owned Outright (Paid Off)' }
      ];
    }
  };
  
  const ownershipTypes = getOwnershipTypes();

  // Determine which fields to show based on ownership type
  // For Financed: Show all fields (interest rate, original price, payment, term)
  // For Lease: Show interest rate, payment, term (NO original price - you don't own it)
  // For Owned: Show original price only (no payments, no interest, no term)
  const showInterestRate = formData.ownershipType === 'financed' || formData.ownershipType === 'lease' || !formData.ownershipType;
  const showOriginalPrincipal = formData.ownershipType !== 'lease' || !formData.ownershipType;
  const showPaymentAmount = formData.ownershipType !== 'owned' || !formData.ownershipType;
  const showTerm = formData.ownershipType !== 'owned' || !formData.ownershipType;
  
  // Check if asset is a vehicle for contextual labels
  const isVehicle = formData.assetCategory === 'vehicle';

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
      <h3 className="text-md font-medium text-gray-900 mb-3">Edit Loan Details</h3>
      
      <form onSubmit={handleSubmit}>
        {/* Asset Category - Full Width */}
        <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Asset Category
            </label>
            <select
              name="assetCategory"
              value={formData.assetCategory}
              onChange={handleChange}
              className="w-full p-2 text-sm border border-gray-300 rounded-md"
              required
            >
            <option value="">Select an Asset Category...</option>
              {assetCategories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

        {/* Ownership Type - REQUIRED for all loans */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Ownership Type <span className="text-red-500">*</span>
          </label>
          <select
            name="ownershipType"
            value={formData.ownershipType}
            onChange={handleChange}
            className="w-full p-2 text-sm border border-gray-300 rounded-md"
            required
          >
            <option value="">Select Ownership Type...</option>
            {ownershipTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {formData.ownershipType === 'financed' && formData.assetCategory === 'vehicle' && '💰 Financed: You own the vehicle but have an auto loan'}
            {formData.ownershipType === 'financed' && (formData.assetCategory === 'home' || formData.assetCategory === 'investment') && '🏠 Mortgaged: You own the property with a mortgage'}
            {formData.ownershipType === 'financed' && !['vehicle', 'home', 'investment'].includes(formData.assetCategory) && '💰 Financed: You own the asset but have a loan'}
            {formData.ownershipType === 'lease' && '📝 Lease: You don\'t own it, just monthly payments'}
            {formData.ownershipType === 'owned' && (formData.assetCategory === 'home' || formData.assetCategory === 'investment') && '✅ Owned: Property is fully paid off'}
            {formData.ownershipType === 'owned' && formData.assetCategory === 'vehicle' && '✅ Owned: Vehicle is paid off'}
            {formData.ownershipType === 'owned' && !['vehicle', 'home', 'investment'].includes(formData.assetCategory) && '✅ Owned: Paid off, no payments or interest'}
            {!formData.ownershipType && '⚠️ Required for accurate net worth calculation'}
          </p>
        </div>

        {/* Asset Value Fields - Show for homes and vehicles (financed or owned) */}
        {(formData.assetCategory === 'home' || formData.assetCategory === 'investment' || formData.assetCategory === 'vehicle') && 
         formData.ownershipType !== 'lease' && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {formData.assetCategory === 'vehicle' ? 'Purchase Price ($)' : 'Home Purchase Price ($)'}
                <span className="text-blue-500 ml-1" title="What you originally paid for the asset">ⓘ</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="purchasePrice"
                value={formData.purchasePrice}
                onChange={handleChange}
                className="w-full p-2 text-sm border border-gray-300 rounded-md"
                placeholder={formData.assetCategory === 'vehicle' ? 'e.g. 35000' : 'e.g. 500000'}
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.assetCategory === 'vehicle' ? 'Original car purchase price' : 'Original home purchase price'}
              </p>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {formData.assetCategory === 'vehicle' ? 'Current Car Value ($)' : 'Current Market Value ($)'}
                <span className="text-blue-500 ml-1" title="Current estimated worth of the asset">ⓘ</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="currentValue"
                value={formData.currentValue}
                onChange={handleChange}
                className="w-full p-2 text-sm border border-gray-300 rounded-md"
                placeholder={formData.assetCategory === 'vehicle' ? 'e.g. 28000' : 'e.g. 550000'}
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.assetCategory === 'vehicle' ? 'Estimated current worth' : 'Current home market value'}
              </p>
            </div>
          </div>
        )}

        {/* Lease-Specific Fields - Only show for vehicle leases */}
        {formData.assetCategory === 'vehicle' && formData.ownershipType === 'lease' && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  MSRP / Vehicle Value ($)
                  <span className="text-blue-500 ml-1" title="Original retail price of the leased vehicle">ⓘ</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="currentValue"
                  value={formData.currentValue}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md"
                  placeholder="e.g. 45000"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Original value of leased vehicle
                </p>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Buyout / Residual Value ($)
                  <span className="text-blue-500 ml-1" title="Purchase option price at lease end">ⓘ</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="buyoutPrice"
                  value={formData.buyoutPrice}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md"
                  placeholder="e.g. 25000"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Price to buy at lease end
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Lease End Date
                </label>
                <input
                  type="date"
                  name="leaseEndDate"
                  value={formData.leaseEndDate}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md"
                />
                <p className="mt-1 text-xs text-gray-500">
                  When your lease expires
                </p>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Annual Mileage Limit
                  <span className="text-blue-500 ml-1" title="Miles allowed per year">ⓘ</span>
                </label>
                <input
                  type="number"
                  name="mileageLimit"
                  value={formData.mileageLimit}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md"
                  placeholder="e.g. 12000"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Miles per year allowed
                </p>
              </div>
            </div>
          </>
        )}
          
        {/* Two Column Grid - Conditionally show fields based on ownership type */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {showInterestRate && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Interest Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                name="interestRate"
                value={formData.interestRate}
                onChange={handleChange}
                className="w-full p-2 text-sm border border-gray-300 rounded-md"
                placeholder="e.g. 4.5"
                required={showInterestRate}
              />
            </div>
          )}
          
          {showOriginalPrincipal && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {(() => {
                  const assetCat = formData.assetCategory;
                  const ownership = formData.ownershipType;
                  
                  if (assetCat === 'vehicle') {
                    return ownership === 'owned' ? 'Purchase Price ($)' : 'Original Loan Amount ($)';
                  } else if (assetCat === 'home' || assetCat === 'investment') {
                    return ownership === 'owned' ? 'Purchase Price ($)' : 'Original Mortgage ($)';
                  } else {
                    return 'Original Loan Amount ($)';
                  }
                })()}
              </label>
              <input
                type="number"
                step="0.01"
                name="originalPrincipal"
                value={formData.originalPrincipal}
                onChange={handleChange}
                className="w-full p-2 text-sm border border-gray-300 rounded-md"
                placeholder={(() => {
                  const assetCat = formData.assetCategory;
                  if (assetCat === 'vehicle') return 'e.g. 35000';
                  if (assetCat === 'home') return 'e.g. 450000';
                  if (assetCat === 'investment') return 'e.g. 350000';
                  return 'e.g. 50000';
                })()}
                required={showOriginalPrincipal}
              />
            </div>
          )}
          
          {showPaymentAmount && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {formData.ownershipType === 'lease' ? 'Monthly Lease Payment ($)' : 'Monthly Payment ($)'}
              </label>
              <input
                type="number"
                step="0.01"
                name="paymentAmount"
                value={formData.paymentAmount}
                onChange={handleChange}
                className="w-full p-2 text-sm border border-gray-300 rounded-md"
                placeholder={(() => {
                  const assetCat = formData.assetCategory;
                  if (assetCat === 'vehicle') return 'e.g. 450';
                  if (assetCat === 'home' || assetCat === 'investment') return 'e.g. 2500';
                  return 'e.g. 500';
                })()}
                required={showPaymentAmount}
              />
            </div>
          )}
          
          {showTerm && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {formData.ownershipType === 'lease' ? 'Lease Term' : 'Loan Term'}
              </label>
              <input
                type="text"
                name="term"
                value={formData.term}
                onChange={handleChange}
                className="w-full p-2 text-sm border border-gray-300 rounded-md"
                placeholder={(() => {
                  const assetCat = formData.assetCategory;
                  if (assetCat === 'vehicle') return formData.ownershipType === 'lease' ? 'e.g. 36 months' : 'e.g. 60 months';
                  if (assetCat === 'home' || assetCat === 'investment') return 'e.g. 30 years';
                  return 'e.g. 5 years';
                })()}
                required={showTerm}
              />
            </div>
          )}
        </div>
          
        {/* Origination Date - Full Width */}
        <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
            Loan Originated Date
            </label>
            <input
            type="date"
            name="originatedDate"
            value={formData.originatedDate}
              onChange={handleChange}
              className="w-full p-2 text-sm border border-gray-300 rounded-md"
            placeholder="MM/DD/YYYY"
            />
        </div>
        
        <div className="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

const WalletCard = ({ account, onUpdate, onDelete, isOwnedByUser = true, syncStatus = {}, onReconnect, onCompleteLoan, viewMode = 'my' }) => {
  const [expanded, setExpanded] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [showLoanDetailsForm, setShowLoanDetailsForm] = useState(false);
  const [isSavingLoanDetails, setIsSavingLoanDetails] = useState(false);
  const [loanDetailsUpdated, setLoanDetailsUpdated] = useState(0);
  const [isSavingOwnership, setIsSavingOwnership] = useState(false);
  
  // Get user context to check if they have a partner
  const { user } = useAuthContext();
  const hasPartner = !!(user?.partner || user?.createdPartner);
  
  // Ref to track if we're currently updating sharing settings
  const isUpdatingRef = useRef(false);
  
  // Local state to track sharing status for immediate UI feedback
  // Initialize from account.sharedWithPartner OR visibilitySettings.showToPartner OR metadata.sharingSettings
  const [isShared, setIsShared] = useState(() => {
    const shared = account.sharedWithPartner || 
                   account.visibilitySettings?.showToPartner || 
                   account.metadata?.sharingSettings?.showToPartner || 
                   false;
    console.log('🔍 WalletCard initial isShared state:', {
      accountId: account._id,
      accountName: account.name,
      isShared: shared,
      sharedWithPartner: account.sharedWithPartner,
      visibilitySettings: account.visibilitySettings,
      metadataSharing: account.metadata?.sharingSettings
    });
    return shared;
  });
  
  // Update local state when account prop changes (but not during active updates)
  useEffect(() => {
    // Skip if we're in the middle of an update to prevent race condition
    if (isUpdatingRef.current) {
      // Silently skip - no need to log every skip
      return;
    }
    
    const sharedStatus = account.sharedWithPartner || 
                        account.visibilitySettings?.showToPartner || 
                        account.metadata?.sharingSettings?.showToPartner || 
                        false;
    // Only log connection status changes, not every prop update
    if (process.env.NODE_ENV === 'development' && prevProps?.syncStatus !== syncStatus) {
      console.log('🔗 WalletCard connection status update:', {
        accountId: account?._id,
        accountName: account?.name,
        connectionStatus: getConnectionStatus(),
        syncStatus: syncStatus
      });
    }
    
    setIsShared(sharedStatus);
  }, [account.sharedWithPartner, account.visibilitySettings, account.metadata, account._id, account.name]);


  // Determine if this is a Plaid account
  const isPlaidAccount = account.isPlaidAccount || !!account.plaidAccountId;
  
  // Get additional account information
  const accountInfo = useMemo(() => {
    if (!account) return null;

    const isPlaidAccount = !!account.isPlaidAccount;
    const isCreditAccount = account.type === 'credit' || account.accountType === 'credit';
    const isLoanAccount = account.type === 'loan' || account.accountType === 'loan';
    const lastUpdate = account.lastUpdated || account.updatedAt;
    
    // Get loan details from various possible sources
    const loanDetails = account.loanDetails || {};
    
    // Extract values with fallbacks
    const interestRate = loanDetails.interestRate || account.interestRate || null;
    const originalAmount = loanDetails.originalPrincipal || account.originalAmount || null;
    const monthlyPayment = loanDetails.paymentAmount || account.monthlyPayment || null;
    const loanTerm = loanDetails.term || account.termLength || null;
    
    // Calculate paid percentage if we have original amount
    let paidPercentage = null;
    let equity = null;
    let remainingTerm = null;
    if (originalAmount && (account.balance || account.currentBalance)) {
      const currentBalance = Math.abs(account.balance || account.currentBalance);
      const paidAmount = originalAmount - currentBalance;
      paidPercentage = ((paidAmount / originalAmount) * 100).toFixed(1);
      // Equity is the amount paid down on the loan
      equity = paidAmount;
      
      // Calculate remaining term based on paid percentage
      if (loanTerm && paidPercentage) {
        const remainingPercentage = 100 - parseFloat(paidPercentage);
        const estimatedRemaining = (loanTerm * remainingPercentage / 100).toFixed(1);
        remainingTerm = estimatedRemaining;
      }
    }
    
    const accountData = {
      name: account.name,
      institution: account.institutionName || 'Unknown Bank',
      mask: account.mask || account.lastFourDigits || '****',
      balance: account.balance || account.currentBalance || 0,
      availableBalance: account.availableBalance || account.balance || account.currentBalance || 0,
      creditLimit: account.creditLimit || null,
      lastUpdate: lastUpdate ? new Date(lastUpdate).toLocaleDateString() : 'Never',
      isPlaid: isPlaidAccount,
      isCredit: isCreditAccount,
      isLoan: isLoanAccount,
      
      // Add loan-specific properties if it's a loan account
      ...(isLoanAccount && {
        interestRate,
        originalAmount,
        monthlyPayment,
        loanTerm,
        paidPercentage,
        equity, // Add equity calculation
        remainingTerm, // Add remaining term calculation
        nextPaymentDue: loanDetails.nextPaymentDue ? new Date(loanDetails.nextPaymentDue).toLocaleDateString() : null
      })
    };
    
    return accountData;
  }, [account, loanDetailsUpdated]);

  // Function to handle statement upload
  const handleStatementUpload = (file) => {
    setProcessing(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('accountId', account._id);
    
    fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/wallet/${account._id}/statement`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          toast.success('Statement processed successfully');
          setShowUpload(false);
          onUpdate();
        } else {
          throw new Error(data.message || 'Error processing statement');
        }
      })
      .catch(err => {
        console.error('Error uploading statement:', err);
        setError(`Error: ${err.message}`);
        toast.error(`Statement upload failed: ${err.message}`);
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDisconnect = async () => {
    // Open the disconnect modal instead of using window.confirm
    setShowDisconnectModal(true);
  };

  const performDisconnect = async (includeTransactions = false) => {
    try {
      // First update the UI immediately for better UX
      if (onDelete) {
        onDelete(account._id);
      }

      const toastId = 'disconnect-toast';
      toast.loading(`${includeTransactions ? 'Deleting account and transactions' : 'Disconnecting account'}...`, { id: toastId });
      
      // Make API call to disconnect the account
      const result = await walletService.disconnectPlaidAccount({
        _id: account._id,
        isPlaidAccount: true,
        plaidAccountId: account.plaidAccountId,
        plaidItemId: account.plaidItemId,
        name: account.name
      }, includeTransactions);
      
      toast.dismiss(toastId);
      
      if (result.success) {
        toast.success(includeTransactions 
          ? 'Account and all transactions deleted successfully' 
          : 'Account disconnected successfully'
        );
        
        // Navigate away if we're currently on this account's page
        if (window.location.pathname.includes(account._id)) {
          navigate('/wallet');
        }
      } else {
        // If the error specifically mentions transaction deletion
        if (result.error?.includes('Failed to delete transactions')) {
          toast.error(`Error: ${result.error}. Please try again.`, {
            duration: 5000
          });
          // Re-add the account to UI since we couldn't complete the operation
          if (onUpdate) {
            onUpdate();
        }
      } else {
        // Show a warning but don't re-add the account to UI
        toast.error(`Account removed from view, but there was an error on the server: ${result.error || 'Unknown error'}`);
        console.error('Server error when disconnecting account:', result.error);
        }
      }
    } catch (error) {
      toast.dismiss('disconnect-toast');
      
      // Show more user-friendly error and try to re-add the account to UI
      const errorMessage = error?.message?.includes('Missing Plaid itemId') 
        ? 'Account removal failed: Database reference error. Try refreshing the page.'
        : `Account removal failed: ${error?.message || 'Unknown error'}`;
        
      toast.error(errorMessage, { duration: 5000 });
      console.error('Error disconnecting Plaid account:', error);
      
      // Try to re-add the account to UI since the operation failed
      if (onUpdate) {
        onUpdate();
      }
    } finally {
      setShowDisconnectModal(false);
    }
  };

  // Delete account with transactions
  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      // Handle Plaid accounts differently - need to disconnect from Plaid first
      if (account.isPlaidAccount) {
        // First, ensure account has Plaid identifiers
        if (!account.plaidAccountId && !account.plaidItemId) {
          // If missing, try to use more available information
          account.plaidAccountId = account._id; // This will at least try to use the account ID
        }
        
        // Use the Plaid disconnect method with transactions
        const response = await walletService.disconnectPlaidAccount(account, true);
        
        if (response.success) {
          toast.success('Account and transactions deleted');
          // Force refresh to update UI
          onUpdate && onUpdate();
        } else {
          // Try alternative approach if first method fails
          try {
            const altResponse = await walletService.deleteAccountWithTransactions(account._id);
            if (altResponse.success) {
              toast.success('Account deleted successfully (alternative method)');
              onUpdate && onUpdate();
            } else {
              throw new Error(altResponse.error || 'Failed to delete account');
            }
          } catch (altError) {
            console.error('Alternative deletion failed:', altError);
            toast.error(`Failed to delete account: ${response.error}`);
          }
        }
      } else {
        // Regular account deletion
        try {
          // Try using the deleteAccountWithTransactions method first
          const response = await walletService.deleteAccountWithTransactions(account._id);
          toast.success('Account and transactions deleted');
          onUpdate && onUpdate();
        } catch (deleteError) {
          console.error('Error with deleteAccountWithTransactions:', deleteError);
          
          // If that fails, fall back to regular delete
          try {
            const fallbackResponse = await walletService.deleteAccount(account._id);
            if (fallbackResponse.success) {
              toast.success('Account deleted (fallback method)');
              onUpdate && onUpdate();
            } else {
              throw new Error(fallbackResponse.error || 'Failed to delete account');
            }
          } catch (fallbackError) {
            console.error('Fallback delete failed:', fallbackError);
            toast.error('Failed to delete account');
          }
        }
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      toast.error('Failed to delete account');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleViewTransactions = () => {
    // For Plaid accounts, make sure to use the wallet ID if available
    if (isPlaidAccount && account.walletId) {
      navigate(`/transactions/account/${account.walletId}`);
    } else {
      navigate(`/transactions/account/${account._id}`);
    }
  };

  // Get account name based on account type
  const getAccountName = () => {
    if (isPlaidAccount) {
      return account.name;
    }
    return account.bankName;
  };

  // Get account type/badge text
  const getAccountType = () => {
    if (isPlaidAccount) {
      return account.subtype ? account.subtype.charAt(0).toUpperCase() + account.subtype.slice(1) : account.type;
    }
    return account.accountType;
  };

  // Get account mask/last four digits
  const getAccountMask = () => {
    if (isPlaidAccount) {
      return account.accountMask ? maskAccountNumber(account.accountMask) : '';
    }
    return account.lastFourDigits ? maskAccountNumber(account.lastFourDigits) : '';
  };

  // Get account balance
  const getBalance = () => {
    // Determine if this is a credit card account
    const isCreditCard = (
      account.accountType === 'credit' || 
      account.accountType === 'credit card' ||
      account.type === 'credit' ||
      account.subtype === 'credit card' ||
      account.subtype === 'credit' ||
      (account.name && account.name.toLowerCase().includes('credit')) ||
      (account.name && account.name.toLowerCase().includes('card')) ||
      (account.name && account.name.toLowerCase().includes('visa')) ||
      (account.name && account.name.toLowerCase().includes('mastercard')) ||
      (account.name && account.name.toLowerCase().includes('amex')) ||
      (account.name && account.name.toLowerCase().includes('american express')) ||
      (account.name && account.name.toLowerCase().includes('costco')) ||
      (account.name && account.name.toLowerCase().includes('discover'))
    );

    // Enhanced credit card balance logic with Chase anomaly detection
    if (isCreditCard) {
      try {
        // **CHASE ANOMALY DETECTION & FIX**
        // Chase sometimes reports currentBalance = availableBalance when card is actually paid off
        const hasChaseAnomaly = (
          account.currentBalance === account.availableBalance && 
          account.currentBalance > 0 &&
          account.currentBalance >= 1000 // Likely credit limit, not debt
        );
        
        let creditCardBalance;
        
        if (hasChaseAnomaly) {
          // For Chase anomalies, return $0 (card is paid off)
          creditCardBalance = 0;
        } else {
          // Use the normal credit card balance (debt amount)
          creditCardBalance = account.currentBalance || 0;
        }
        
        return creditCardBalance;
        
      } catch (error) {
        console.error('WalletCard: Error in credit card balance calculation:', error);
        // Fallback to simple logic if there's any error
        return account.currentBalance || 0;
      }
    }

    // For non-credit accounts, use the standard fallback chain
    if (isPlaidAccount) {
      return account.currentBalance || account.availableBalance || account.balance || 0;
    }
    return account.balance || 0;
  };

  // Helper functions for connection status
  const getInstitutionName = () => {
    return account.institutionName || account.bankName || 'Unknown Bank';
  };

  const getConnectionStatus = () => {
    if (!isPlaidAccount) return { status: 'manual', message: 'Manual Account' };
    
    // Check if account is stale (lastUpdated > 7 days ago)
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const lastUpdatedDate = account.lastUpdated || account.updatedAt || account.lastSyncDate;
    let isStale = false;
    
    if (lastUpdatedDate) {
      const lastUpdated = new Date(lastUpdatedDate).getTime();
      if (!isNaN(lastUpdated) && lastUpdated < sevenDaysAgo) {
        isStale = true;
      }
    } else {
      // No lastUpdated date means account is stale
      isStale = true;
    }
    
    const institutionName = getInstitutionName();
    let institutionStatus = syncStatus[institutionName];
    
    // If exact match fails, try fuzzy matching
    if (!institutionStatus && Object.keys(syncStatus).length > 0) {
      const syncKeys = Object.keys(syncStatus);
      
      // Try to find a match by checking if the institution name contains any of the sync status keys
      // or if any sync status key contains the institution name
      const fuzzyMatch = syncKeys.find(key => {
        const keyLower = key.toLowerCase();
        const nameLower = institutionName.toLowerCase();
        
        // Check for partial matches in both directions
        return keyLower.includes(nameLower) || nameLower.includes(keyLower) ||
               // Handle common variations
               (keyLower.includes('ally') && nameLower.includes('ally')) ||
               (keyLower.includes('chase') && nameLower.includes('chase')) ||
               (keyLower.includes('citibank') && nameLower.includes('citibank')) ||
               (keyLower.includes('citi') && nameLower.includes('citi')) ||
               (keyLower.includes('us bank') && nameLower.includes('us bank')) ||
               (keyLower.includes('u.s. bank') && nameLower.includes('us bank')) ||
               (keyLower.includes('us bank') && nameLower.includes('u.s. bank'));
      });
      
      if (fuzzyMatch) {
        institutionStatus = syncStatus[fuzzyMatch];
      }
    }
    
    // If account is stale, always return expired status
    if (isStale) {
      return { status: 'expired', message: 'Reconnect' };
    }
    
    if (!institutionStatus) {
      return { status: 'unknown', message: 'Reconnect' };
    }
    
    if (institutionStatus.success) {
      return { status: 'connected', message: 'Connected' };
    }
    
    if (institutionStatus.error === 'CONNECTION_EXPIRED') {
      return { status: 'expired', message: 'Reconnect' };
    }
    
    // Do NOT scare users for rate-limited states
    if (institutionStatus.error === 'RATE_LIMITED') {
      return { status: 'connected', message: 'Rate limited; will retry automatically' };
    }
    
    return { status: 'disconnected', message: 'Reconnect' };
  };

  const getConnectionStatusStyle = () => {
    const { status } = getConnectionStatus();
    
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'disconnected':
        return 'bg-red-100 text-red-800';
      case 'unknown':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getConnectionStatusIcon = () => {
    const { status } = getConnectionStatus();
    
    switch (status) {
      case 'connected':
        return <LinkIcon className="h-3 w-3 mr-1" />;
      case 'expired':
        return <ExclamationTriangleIcon className="h-3 w-3 mr-1" />;
      case 'disconnected':
        return <XMarkIcon className="h-3 w-3 mr-1" />;
      case 'unknown':
        return <QuestionMarkCircleIcon className="h-3 w-3 mr-1" />;
      default:
        return <LinkIcon className="h-3 w-3 mr-1" />;
    }
  };

  const getConnectionStatusText = () => {
    const { status, message } = getConnectionStatus();
    // Always show "Reconnect" for expired/disconnected/unknown statuses
    if (status === 'expired' || status === 'disconnected' || status === 'unknown') {
      return 'Reconnect';
    }
    return message;
  };

  // Get color scheme based on account type
  const getCardColorScheme = () => {
    return isPlaidAccount ? 'bg-indigo-100 text-indigo-800' : 'bg-purple-100 text-purple-800';
  };

  // Get account type styling
  const getAccountTypeClass = () => {
    if (isPlaidAccount) {
      return 'border-indigo-200 border-opacity-50';
    }
    
    // Determine if this is a credit card account
    const isCreditCard = (
      account.accountType === 'credit' || 
      account.accountType === 'credit card' ||
      account.type === 'credit' ||
      account.subtype === 'credit card' ||
      account.subtype === 'credit' ||
      (account.name && account.name.toLowerCase().includes('credit')) ||
      (account.name && account.name.toLowerCase().includes('card')) ||
      (account.name && account.name.toLowerCase().includes('visa')) ||
      (account.name && account.name.toLowerCase().includes('mastercard')) ||
      (account.name && account.name.toLowerCase().includes('amex')) ||
      (account.name && account.name.toLowerCase().includes('american express')) ||
      (account.name && account.name.toLowerCase().includes('costco')) ||
      (account.name && account.name.toLowerCase().includes('discover'))
    );

    // Determine if this is a loan account
    const isLoanAccount = (
      account.accountType === 'loan' || 
      account.type === 'loan' ||
      account.subtype === 'loan' ||
      account.accountType === 'mortgage' ||
      account.type === 'mortgage' ||
      account.subtype === 'mortgage' ||
      account.accountType === 'student' ||
      account.subtype === 'student' ||
      (account.name && account.name.toLowerCase().includes('loan')) ||
      (account.name && account.name.toLowerCase().includes('mortgage')) ||
      (account.name && account.name.toLowerCase().includes('student')) ||
      (account.name && account.name.toLowerCase().includes('auto')) ||
      (account.name && account.name.toLowerCase().includes('personal'))
    );
    
    if (isCreditCard) {
      return 'border-red-200 border-opacity-50';
    }
    
    if (isLoanAccount) {
      return 'border-amber-200 border-opacity-50';
    }
    
    switch (account.accountType?.toLowerCase()) {
      case 'checking':
        return 'border-blue-200 border-opacity-50';
      case 'savings':
        return 'border-green-200 border-opacity-50';
      case 'investment':
        return 'border-purple-200 border-opacity-50';
      default:
        return 'border-gray-200';
    }
  };

  // Derived credit card details
  const creditLimit = account.creditLimit || account.credit_limit;
  const availableCredit = (typeof creditLimit === 'number' && typeof account.currentBalance === 'number')
    ? Math.max(creditLimit - account.currentBalance, 0)
    : null;
  const minPayment = account.monthlyPayment || account.minPayment || null;
  const nextDue = account.nextPaymentDue || account.paymentDue || null;

  // Handle sharing account with partner
  const handleShareAccount = async () => {
    try {
      setIsSharing(true);
      
      // Set the updating flag to prevent useEffect from overwriting our change
      isUpdatingRef.current = true;
      // Lock state updates during toggle operation (no logging needed)
      
      // Toggle the sharing status based on current local state
      const newSharingStatus = !isShared;
      
      const settings = {
        showToPartner: newSharingStatus,
        showTransactions: newSharingStatus,
        showBalance: newSharingStatus,
        showInNetWorth: newSharingStatus
      };
      
      const result = await walletService.updateSharingSettings(account._id, settings);
      
      // API response received (no logging needed in production)
      
      if (result.success) {
        // Get the updated account from the API response
        const updatedAccount = result.data;
        
        // Update the local account object with data from server
        if (updatedAccount) {
          // Handle both regular accounts and Plaid accounts
          if (updatedAccount.visibilitySettings) {
            // Regular account
            account.sharedWithPartner = updatedAccount.sharedWithPartner;
            account.visibilitySettings = updatedAccount.visibilitySettings;
          } else if (updatedAccount.metadata?.sharingSettings) {
            // Plaid account
            account.metadata = account.metadata || {};
            account.metadata.sharingSettings = updatedAccount.metadata.sharingSettings;
            account.sharedWithPartner = updatedAccount.metadata.sharingSettings.showToPartner;
          }
        }
        
        // Update local state to persist toggle UI
        setIsShared(newSharingStatus);
        
        toast.success(newSharingStatus ? 'Account shared with partner!' : 'Account unshared from partner');
        
        // Unlock state updates after a delay to allow the state to settle
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 500);
        
        // Don't call onUpdate() to avoid race condition with state updates
        // The local state has been updated and will persist
      } else {
        throw new Error(result.error || 'Failed to update sharing settings');
      }
    } catch (error) {
      console.error('Error in handleShareAccount:', error);
      toast.error('Failed to update sharing: ' + error.message);
      // Revert the toggle on error
      const revertedStatus = !isShared;
      setIsShared(revertedStatus);
      // Unlock the ref on error
      isUpdatingRef.current = false;
    } finally {
      setIsSharing(false);
    }
  };

  // Handle saving loan details
  const handleSaveLoanDetails = async (loanDetails) => {
    try {
      setIsSavingLoanDetails(true);
      
      // Clean up the loan details - remove empty strings for enum fields
      const cleanedLoanDetails = { ...loanDetails };
      
      // Remove any field that is empty string, null, or undefined
      Object.keys(cleanedLoanDetails).forEach(key => {
        const value = cleanedLoanDetails[key];
        if (value === '' || value === null || value === undefined) {
          delete cleanedLoanDetails[key];
        }
      });
      
      toast.loading('Saving loan details...', { id: 'loan-save' });
      
      const result = await walletService.updateLoanDetails(account._id, cleanedLoanDetails);
      
      toast.dismiss('loan-save');
      
      if (result.success) {
        // Get the updated account data from the server response
        const updatedAccount = result.data;
        
        // Update the account object properties directly
        if (updatedAccount.loanDetails) {
          account.loanDetails = { ...updatedAccount.loanDetails };
        }
        
        // Update backup fields from both nested and direct locations
        if (updatedAccount.loanDetails?.interestRate !== undefined) {
          account.interestRate = updatedAccount.loanDetails.interestRate;
        }
        if (updatedAccount.loanDetails?.originalPrincipal !== undefined) {
          account.originalAmount = updatedAccount.loanDetails.originalPrincipal;
        }
        if (updatedAccount.loanDetails?.paymentAmount !== undefined) {
          account.monthlyPayment = updatedAccount.loanDetails.paymentAmount;
        }
        if (updatedAccount.loanDetails?.term !== undefined) {
          account.termLength = updatedAccount.loanDetails.term;
        }
        
        // Also check direct fields in the response
        if (updatedAccount.interestRate !== undefined) {
          account.interestRate = updatedAccount.interestRate;
        }
        if (updatedAccount.originalAmount !== undefined) {
          account.originalAmount = updatedAccount.originalAmount;
        }
        if (updatedAccount.monthlyPayment !== undefined) {
          account.monthlyPayment = updatedAccount.monthlyPayment;
        }
        
        toast.success('Loan details updated successfully');
        
        // Force re-render of accountInfo by updating the trigger
        setLoanDetailsUpdated(prev => prev + 1);
        
        // Close the form
        setShowLoanDetailsForm(false);
        
        // 🔄 TRIGGER ASSET REFRESH: Refresh liability assets in Cash Flow if function exists
        if (typeof window.refreshLiabilityAssets === 'function') {
          setTimeout(() => {
            window.refreshLiabilityAssets();
          }, 500); // Small delay to ensure data is propagated
        }
        
        // DON'T call onUpdate() here - it would refetch data and overwrite our local updates
        // The local account object has been updated correctly, so just let the UI re-render
      } else {
        toast.error(`Failed to update: ${result.error}`);
        throw new Error(result.error || 'Failed to update loan details');
      }
    } catch (error) {
      toast.dismiss('loan-save');
      
      console.error('Error updating loan details:', error);
      
      // Show a more user-friendly error message
      if (error.message.includes('404') || error.message.includes('not found')) {
        toast.error('Could not update loan details - account not found. Please refresh the page.');
      } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
        toast.error('Your session may have expired. Please log in again.');
      } else {
        toast.error(`Failed to update: ${error.message}`);
      }
    } finally {
      setIsSavingLoanDetails(false);
    }
  };

  // Handle toggling account ownership (simple toggle)
  const handleToggleOwnership = async () => {
    try {
      setIsSavingOwnership(true);
      
      // Toggle between personal and joint
      const currentOwnership = account.ownershipType || 'personal';
      const newOwnership = currentOwnership === 'joint' ? 'personal' : 'joint';
      
      toast.loading(`Marking account as ${newOwnership}...`, { id: 'ownership-save' });
      
      const result = await walletService.updateAccountOwnership(account._id, {
        ownershipType: newOwnership,
        updateExistingTransactions: true // Always update existing transactions
      });
      
      toast.dismiss('ownership-save');
      
      if (result.success) {
        // Update the account object locally
        account.ownershipType = newOwnership;
        account.isJointAccount = (newOwnership === 'joint');
        
        // Force re-render by updating state
        setLoanDetailsUpdated(prev => prev + 1);
        
        toast.success(
          newOwnership === 'joint'
            ? 'Account marked as Joint - all transactions updated'
            : 'Account marked as Personal - all transactions updated'
        );
        
        // Trigger parent refresh to update the list
        if (onUpdate) {
          setTimeout(() => onUpdate(), 500);
        }
      } else {
        toast.error(`Failed to update: ${result.error}`);
        throw new Error(result.error || 'Failed to update ownership');
      }
    } catch (error) {
      toast.dismiss('ownership-save');
      console.error('Error updating ownership:', error);
      toast.error(`Failed to update: ${error.message}`);
    } finally {
      setIsSavingOwnership(false);
    }
  };

  // Determine if this is a credit card account (consistent with getAccountInfo)
  const isCreditCard = (
    account.accountType === 'credit' || 
    account.accountType === 'credit card' ||
    account.type === 'credit' ||
    account.subtype === 'credit card' ||
    account.subtype === 'credit' ||
    (account.name && account.name.toLowerCase().includes('credit')) ||
    (account.name && account.name.toLowerCase().includes('card')) ||
    (account.name && account.name.toLowerCase().includes('visa')) ||
    (account.name && account.name.toLowerCase().includes('mastercard')) ||
    (account.name && account.name.toLowerCase().includes('amex')) ||
    (account.name && account.name.toLowerCase().includes('american express')) ||
    (account.name && account.name.toLowerCase().includes('costco')) ||
    (account.name && account.name.toLowerCase().includes('discover'))
  );

  // Determine if this is a loan account (consistent with getAccountInfo)
  const isLoanAccount = (
    account.accountType === 'loan' || 
    account.type === 'loan' ||
    account.subtype === 'loan' ||
    account.accountType === 'mortgage' ||
    account.type === 'mortgage' ||
    account.subtype === 'mortgage' ||
    account.accountType === 'student' ||
    account.subtype === 'student' ||
    (account.name && account.name.toLowerCase().includes('loan')) ||
    (account.name && account.name.toLowerCase().includes('mortgage')) ||
    (account.name && account.name.toLowerCase().includes('student')) ||
    (account.name && account.name.toLowerCase().includes('auto')) ||
    (account.name && account.name.toLowerCase().includes('personal'))
  );

  const isDepository = (
    (account.type === 'depository' && (account.subtype === 'checking' || account.subtype === 'savings')) ||
    ['checking','savings'].includes((account.accountType || '').toLowerCase())
  );

  const showLoanMissingCTA = isLoanAccount && (
    !accountInfo.originalAmount || !accountInfo.interestRate || !accountInfo.loanTerm
  );

  // Check if this loan is incomplete
  const loanComplete = isLoanComplete(account);
  const missingFields = getMissingLoanFields(account);
  const isIncomplete = isLoanAccount && !loanComplete;

  return (
    <div className={`bg-white rounded-xl shadow-lg border ${isIncomplete ? 'border-amber-400 border-2' : 'border-gray-300'} hover:shadow-xl hover:border-gray-400 transition-all duration-300 ${getAccountTypeClass()} ${isPlaidAccount ? 'border-l-4 border-l-indigo-500' : ''} w-full h-full flex flex-col overflow-hidden ${expanded ? 'min-h-[120px] sm:min-h-[140px]' : 'min-h-[90px] sm:min-h-[110px]'}`}>
      {/* Incomplete Loan Alert Banner */}
      {isIncomplete && (
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 px-3 py-2 border-b border-amber-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
            <span className="text-xs font-medium text-amber-900">
              Setup incomplete - Not included in reports
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onCompleteLoan) onCompleteLoan(account);
              }}
              className="text-xs text-amber-700 hover:text-amber-900 underline font-medium"
              title={`Missing: ${missingFields.map(f => f.label).join(', ')}`}
            >
              Complete Now →
            </button>
          </div>
          <div className="relative group">
            <QuestionMarkCircleIcon className="h-4 w-4 text-amber-600 cursor-help" />
            <div className="absolute right-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
              <p className="font-semibold mb-1">Missing Required Fields:</p>
              <ul className="list-disc list-inside space-y-1">
                {missingFields.map((field, idx) => (
                  <li key={idx}>{field.label}: {field.description}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Top Header - Bank, Account Number, Account Name */}
      <div className="p-1 sm:p-2 md:p-3 border-b border-gray-100">
        <div className="flex items-center justify-between gap-1 sm:gap-2">
          <div className="flex items-center space-x-1 sm:space-x-2 min-w-0 flex-1">
            {account.institutionBranding?.logo ? (
              // eslint-disable-next-line jsx-a11y/alt-text
              <img src={account.institutionBranding.logo} className="h-3 w-3 sm:h-4 sm:w-4 object-contain rounded flex-shrink-0" />
            ) : (
              <BuildingLibraryIcon className="h-3 w-3 sm:h-4 sm:w-4 text-black flex-shrink-0" />
            )}
            <span className="text-[10px] sm:text-sm font-bold text-black truncate min-w-0">
              {isPlaidAccount && account.institutionName ? account.institutionName : (account.bankName || 'Manual Account')}
            </span>
            {isIncomplete && (
              <span className="inline-flex items-center px-1 sm:px-2 py-0.5 rounded-full text-[8px] sm:text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300 flex-shrink-0">
                ⚠️ Incomplete
              </span>
            )}
            {/* Joint Account Tag */}
            {account.__isJoint && (
              <span className="inline-flex items-center px-1 sm:px-2 py-0.5 rounded-full text-[8px] sm:text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 flex-shrink-0">
                Joint
              </span>
            )}
            {/* Owner Name Tag (for non-joint shared accounts) */}
            {!account.__isJoint && account.__ownerName && (
              <span className="inline-flex items-center px-1 sm:px-2 py-0.5 rounded-full text-[8px] sm:text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300 flex-shrink-0">
                {account.__ownerName}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-1 sm:space-x-3 flex-shrink-0 ml-1">
            <span className="text-[9px] sm:text-xs font-medium text-gray-700 truncate">{getAccountName()}</span>
          </div>
        </div>
      </div>

      {/* Main Content - Responsive Layout */}
      <div className="flex-1 p-1 sm:p-2 md:p-3">
        <div className="flex flex-col items-center justify-center h-full">
          {/* Balance Section - Centered */}
          <div className="flex flex-col items-center w-full">
            {isDepository || isLoanAccount || isCreditCard ? (
              <>
                {/* Vertical centered layout for all screen sizes to prevent cutoff */}
                <div className="flex flex-col items-center">
                  <span className="text-[9px] sm:text-xs font-medium text-gray-600 text-center mb-0.5">
                    {isCreditCard ? 'Current Stmt' : 'Balance'}
                  </span>
                  <span className={`text-sm sm:text-base md:text-xl lg:text-2xl font-extrabold text-center ${
                    isCreditCard
                      ? (getBalance() === 0 ? 'text-black' : 'text-red-600')
                      : (isLoanAccount ? 'text-red-600' : 'text-black')
                  }`}>
                    {isCreditCard
                      ? (getBalance() === 0 ? '$0.00' : formatDebt(getBalance()))
                      : (isLoanAccount ? formatDebt(getBalance()) : formatCurrency(getBalance()))}
                  </span>
                </div>
                {/* Sub-rows under balance */}
                {isDepository && (
                  <>
                    <div className="mt-0.5 sm:mt-1 text-[8px] sm:text-xs text-gray-600 flex gap-1 sm:gap-2 md:gap-4 flex-wrap justify-center">
                      <span>Last: {accountInfo.lastUpdate}</span>
                      <span>Acct ••••{account.mask || account.lastFourDigits || ''}</span>
                      {account.isoCurrencyCode && <span>{account.isoCurrencyCode}</span>}
                    </div>
                    {/* Badges moved from header */}
                    <div className="mt-0.5 sm:mt-1 md:mt-2 flex items-center justify-center gap-1 sm:gap-2">
                      {isPlaidAccount && (
                        <>
                          <span className={`inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-xs font-medium ${getConnectionStatusStyle()}`}>
                            {getConnectionStatusIcon()}
                            <span className="hidden sm:inline ml-1">{getConnectionStatusText()}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </>
                )}
                {isCreditCard && (
                  <>
                    <div className="mt-0.5 sm:mt-1 text-[8px] sm:text-xs text-gray-700 flex flex-col gap-0.5 items-center">
                      {account.lastStatementBalance !== undefined && account.lastStatementBalance !== null && (
                        <span className="text-gray-600">
                          Last Stmt: {formatCurrency(account.lastStatementBalance)}
                        </span>
                      )}
                      <span>Last: {accountInfo.lastUpdate}</span>
                      {minPayment && <span>Min: {formatCurrency(minPayment)}</span>}
                    </div>
                    {/* Badges moved from header */}
                    <div className="mt-0.5 sm:mt-1 md:mt-2 flex items-center justify-center gap-1 sm:gap-2">
                      {isPlaidAccount && (
                        <>
                          <span className={`inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-xs font-medium ${getConnectionStatusStyle()}`}>
                            {getConnectionStatusIcon()}
                            <span className="hidden sm:inline ml-1">{getConnectionStatusText()}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </>
                )}
                {isLoanAccount && (
                  <div className="mt-0.5 sm:mt-1 text-[8px] sm:text-xs text-gray-700">
                    <div className="mb-0.5 text-center">Last: {accountInfo.lastUpdate}</div>
                    {/* Badges moved from header */}
                    <div className="mb-0.5 sm:mb-1 flex items-center justify-center gap-1 sm:gap-2">
                      {isPlaidAccount && (
                        <span className={`inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-xs font-medium ${getConnectionStatusStyle()}`}>
                          {getConnectionStatusIcon()}
                          <span className="hidden sm:inline ml-1">{getConnectionStatusText()}</span>
                        </span>
                      )}
                    </div>
                    {showLoanMissingCTA && (
                      <div className="mb-0.5"><button onClick={() => setShowLoanDetailsForm(true)} className="underline text-[8px] sm:text-xs text-indigo-600 hover:text-indigo-800">Add details</button></div>
                    )}
                    <div className="mt-0.5 text-[8px] sm:text-xs font-semibold text-gray-700 text-center">Loan Details:</div>
                    <div className="mt-0.5 space-y-0.5">
                      {/* Centered layout to prevent text overflow */}
                      <div className="flex justify-center items-center gap-2">
                        <span className="text-gray-600">Equity</span>
                        <span className={`font-medium text-[8px] sm:text-xs ${accountInfo.equity !== null ? 'text-green-600' : ''}`}>{accountInfo.equity !== null ? formatCurrency(accountInfo.equity) : 'N/A'}</span>
                      </div>
                      <div className="flex justify-center items-center gap-2">
                        <span className="text-gray-600">Payment</span>
                        <span className="font-medium text-[8px] sm:text-xs">{accountInfo.monthlyPayment ? formatCurrency(accountInfo.monthlyPayment) : 'N/A'}</span>
                      </div>
                      <div className="flex justify-center items-center gap-2">
                        <span className="text-gray-600">Balance</span>
                        <span className="font-medium text-[8px] sm:text-xs text-red-600">{formatCurrency(Math.abs(accountInfo.balance || 0))}</span>
                      </div>
                      <div className="flex justify-center items-center gap-2">
                        <span className="text-gray-600">Original</span>
                        <span className="font-medium text-[8px] sm:text-xs">{accountInfo.originalAmount ? formatCurrency(accountInfo.originalAmount) : 'N/A'}</span>
                      </div>
                      <div className="flex justify-center items-center gap-2">
                        <span className="text-gray-600">Rate</span>
                        <span className="font-medium text-[8px] sm:text-xs">{accountInfo.interestRate ? `${Number(accountInfo.interestRate).toFixed(2)}%` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-center items-center gap-2">
                        <span className="text-gray-600">Term</span>
                        <span className="font-medium text-[8px] sm:text-xs">{accountInfo.loanTerm ? `${accountInfo.loanTerm}` : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <span className="text-xs font-medium text-gray-600 mb-1">Balance</span>
                <div className="py-2">
                  <p className={`text-2xl font-extrabold ${
                    isCreditCard
                      ? (getBalance() === 0 ? 'text-black' : 'text-red-600')
                      : (isLoanAccount ? 'text-red-600' : 'text-black')
                  }`}>
                    {isCreditCard
                      ? (getBalance() === 0 ? '$0.00' : formatDebt(getBalance()))
                      : (isLoanAccount ? formatDebt(getBalance()) : formatCurrency(getBalance()))}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Action Button */}
      <div className="p-1 sm:p-2 md:p-3 pt-0">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full text-[9px] sm:text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center bg-indigo-50 px-1.5 sm:px-2 py-0.5 sm:py-1 md:py-1.5 rounded-lg transition-all duration-200 hover:bg-indigo-100 border border-indigo-200 hover:border-indigo-300"
        >
          {expanded ? 'Hide Details' : 'Show Details'} 
          {expanded ? (
            <ChevronUpIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 ml-0.5 sm:ml-1" />
          ) : (
            <ChevronDownIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 ml-0.5 sm:ml-1" />
          )}
        </button>
        
        {expanded && (
          <div className="mt-1 sm:mt-2 space-y-1 sm:space-y-1.5 bg-gray-50 rounded-lg p-1.5 sm:p-2.5 border border-gray-200">
            {/* Card details section */}
            {isCreditCard && (
              <div className="text-[9px] sm:text-xs grid grid-cols-2 gap-x-2 sm:gap-x-3 gap-y-0.5 sm:gap-y-1">
                {typeof creditLimit === 'number' && (<><span className="text-gray-600">Limit</span><span className="text-right font-medium">{formatCurrency(creditLimit)}</span></>)}
                {typeof availableCredit === 'number' && (<><span className="text-gray-600">Available</span><span className="text-right font-medium text-green-600">{formatCurrency(availableCredit)}</span></>)}
                {minPayment && (<><span className="text-gray-600">Min Payment</span><span className="text-right font-medium">{formatCurrency(minPayment)}</span></>)}
                {nextDue && (<><span className="text-gray-600">Next Due</span><span className="text-right font-medium">{nextDue}</span></>)}
                {account.lastStatementBalance && (<><span className="text-gray-600">Last Statement</span><span className="text-right font-medium">{formatCurrency(account.lastStatementBalance)}</span></>)}
                {account.lastStatementDate && (<><span className="text-gray-600">Statement Date</span><span className="text-right font-medium">{new Date(account.lastStatementDate).toLocaleDateString()}</span></>)}
                <div className="col-span-2 flex gap-1 sm:gap-2 pt-1 sm:pt-2">
                  <button
                    className="text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                    onClick={() => window.open('https://www.chase.com/pay-credit-card', '_blank')}
                  >
                    Pay card
                  </button>
                  <button
                    className="text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded bg-gray-600 text-white hover:bg-gray-700"
                    onClick={handleViewTransactions}
                  >
                    Transactions
                  </button>
                </div>
              </div>
            )}
            {isLoanAccount && (
              <div className="text-[9px] sm:text-xs grid grid-cols-2 gap-x-2 sm:gap-x-3 gap-y-0.5 sm:gap-y-1">
                <span className="text-gray-600">Original</span><span className="text-right font-medium">{accountInfo.originalAmount ? formatCurrency(accountInfo.originalAmount) : 'N/A'}</span>
                <span className="text-gray-600">Balance</span><span className="text-right font-medium text-red-600">{formatCurrency(Math.abs(accountInfo.balance))}</span>
                <span className="text-gray-600">Rate</span><span className="text-right font-medium">{accountInfo.interestRate ? `${Number(accountInfo.interestRate).toFixed(2)}%` : 'N/A'}</span>
                <span className="text-gray-600">Payment</span><span className="text-right font-medium">{accountInfo.monthlyPayment ? formatCurrency(accountInfo.monthlyPayment) : 'N/A'}</span>
                {showLoanMissingCTA && (
                  <div className="col-span-2 text-[11px] text-gray-600">Payment reflects the recurring installment. Principal may not be provided by the institution.</div>
                )}
                <div className="col-span-2 flex gap-1 sm:gap-2 pt-1 sm:pt-2">
                  <button
                    className="text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded bg-gray-600 text-white hover:bg-gray-700"
                    onClick={handleViewTransactions}
                  >
                    Transactions
                  </button>
                  <button
                    className="text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                    onClick={() => setShowLoanDetailsForm(true)}
                  >
                    Edit details
                  </button>
                </div>
              </div>
            )}
            {!isCreditCard && !isLoanAccount && (
              <div className="text-[9px] sm:text-xs text-gray-600">No additional details.</div>
            )}
            
            {/* Joint Account Toggle - only show in "My Wallet" view for owned accounts */}
            {isOwnedByUser && viewMode === 'my' && (
              <div className="w-full p-1.5 sm:p-2 md:p-3 bg-white border border-gray-200 rounded-md">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-900">Joint Account</p>
                    <p className="text-[9px] sm:text-xs text-gray-500 mt-0.5">
                      {account.ownershipType === 'joint' || account.isJointAccount
                        ? 'Shared with partner - appears in "Our Dashboard"'
                        : 'Personal account - appears in "My Dashboard"'
                      }
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggleOwnership()}
                    disabled={isSavingOwnership}
                    className={`inline-flex items-center justify-center px-2 py-1 sm:px-3 sm:py-1.5 text-[9px] sm:text-xs font-medium rounded-md border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 ${
                      account.ownershipType === 'joint' || account.isJointAccount
                        ? 'bg-blue-600 text-white border-blue-700 hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    } ${isSavingOwnership ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    role="switch"
                    aria-checked={account.ownershipType === 'joint' || account.isJointAccount}
                  >
                    {account.ownershipType === 'joint' || account.isJointAccount ? 'On' : 'Off'}
                  </button>
                </div>
              </div>
            )}
            
            {/* Share toggle - only show in "My Wallet" view */}
            {hasPartner && isOwnedByUser && viewMode === 'my' && (
              <div className="w-full p-1.5 sm:p-2 md:p-3 bg-white border border-gray-200 rounded-md">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-900">Share with Partner</p>
                    <p className="text-[9px] sm:text-xs text-gray-500 mt-0.5">
                      Share this account with {user?.partner?.firstName || user?.createdPartner?.firstName || 'partner'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleShareAccount}
                    disabled={isSharing}
                    className={`inline-flex items-center justify-center px-2 py-1 sm:px-3 sm:py-1.5 text-[9px] sm:text-xs font-medium rounded-md border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-1 ${
                      isShared
                        ? 'bg-green-600 text-white border-green-700 hover:bg-green-700'
                        : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    } ${isSharing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    role="switch"
                    aria-checked={isShared}
                  >
                    {isShared ? 'On' : 'Off'}
                  </button>
                </div>
              </div>
            )}
            
            {/* Partner shared indicator (read-only) */}
            {hasPartner && !isOwnedByUser && (
              <div className="w-full px-2 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-700 flex items-center justify-center border border-purple-200">
                <UserGroupIcon className="h-3 w-3 mr-1" />
                Shared by Partner
              </div>
            )}
            
            {/* Connection Status Alert for Expired/Disconnected Connections */}
            {isPlaidAccount && (getConnectionStatus().status === 'expired' || getConnectionStatus().status === 'disconnected') && (
              <div className="w-full p-2 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center space-x-2">
                  {getConnectionStatus().status === 'expired' ? (
                    <ExclamationTriangleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
                  ) : (
                    <XMarkIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className="text-xs font-medium text-red-800">
                      {getConnectionStatus().status === 'expired' ? 'Connection Expired' : 'Account Disconnected'}
                    </p>
                    <p className="text-xs text-red-600">Please reconnect to update balances</p>
                  </div>
                  <button
                    onClick={onReconnect}
                    className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Reconnect
                  </button>
                </div>
              </div>
            )}
            
            {/* Management buttons */}
            <div className="grid grid-cols-2 gap-1 sm:gap-1.5">
              {isOwnedByUser ? (
                isPlaidAccount ? (
                <button
                  onClick={handleDisconnect}
                  className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors duration-200"
                >
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={() => setShowUpload(true)}
                  className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors duration-200"
                >
                  + Statement
                </button>
                )
              ) : (
                <div className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-xs font-medium rounded-md bg-gray-200 text-gray-500 flex items-center justify-center">
                  Partner Account
                </div>
              )}
              {onDelete && isOwnedByUser && (
                <button
                  onClick={handleDelete}
                  className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors duration-200"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {showUpload && (
        <StatementUpload
          onUpload={handleStatementUpload}
          onCancel={() => setShowUpload(false)}
          isProcessing={processing}
        />
      )}
      
      {error && (
        <div className="mt-2 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <Portal>
          <div className="fixed inset-0 z-50 overflow-auto bg-gray-500 bg-opacity-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 border border-gray-100">
              <div className="absolute top-4 right-4">
                <button 
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Account</h3>
              <p className="text-sm text-gray-500 mb-4">Are you sure you want to delete this account?</p>

              <div className="space-y-3">
                <button
                  onClick={() => confirmDelete()}
                  className="w-full px-4 py-2 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm hover:bg-red-100"
                >
                  Delete account and all transactions
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Disconnect Modal */}
      <DisconnectModal 
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onDisconnect={() => performDisconnect(false)}
        onDisconnectWithTransactions={() => performDisconnect(true)}
        accountName={getAccountName()}
      />

      {showLoanDetailsForm && (
        <LoanDetailsForm
          account={account}
          onSave={handleSaveLoanDetails}
          onCancel={() => setShowLoanDetailsForm(false)}
          isLoading={isSavingLoanDetails}
        />
      )}
    </div>
  );
};

export default WalletCard; 